import { useMatch, useParams } from "react-router-dom";

/**
 * The single source of truth for the tenant-scoped link base in the v1 tree.
 *
 * Returns `/${tenantSlug}` at the root mount and `/${tenantSlug}/v1` at the
 * parked mount — the prefix every in-app v1 link and `navigate()` target is
 * built from. The v1 analog of `useTenantBase()` (which does the same job for
 * the v2 tree); call it in place of hand-rolling `` `/${tenantSlug}/...` ``.
 *
 * WHY THIS EXISTS — v1's shell was pathname-coupled. `Sidebar` built its links
 * from a hardcoded `` `/${tenantSlug}` ``, and react-router derives a
 * `<NavLink>`'s `isActive` by comparing `to` against the live `pathname`. Mount
 * the same tree at `/:tenantSlug/v1` and every `to` still points at
 * `/acme/dashboard` while the user stands at `/acme/v1/dashboard`: `isActive`
 * goes false on EVERY nav row and the sidebar silently loses its active
 * highlight at the parked mount. Same class of bug in `AppShell`'s
 * `useMatch("/:tenantSlug/candidates/:candidateId")`, which gates the
 * candidate-detail shell's whole scroll model.
 *
 * Deriving the base from the CURRENT pathname instead of hardcoding it makes v1
 * render pixel-identically at both mounts — only the hrefs differ, and an href
 * is invisible to a screenshot. That is what lets the v1 visual suite pass
 * unchanged at the parked mount, confirming parking v1 is pixel-safe.
 *
 * It also keeps the parked tree self-consistent: a link built from this base
 * keeps the user inside whichever mount they entered through, instead of
 * ejecting them from `/acme/v1/...` back to the root mount on the first click.
 * That is the difference between v1 being parked and v1 being reachable-but-
 * unusable once the root mount stops being v1.
 *
 * SCOPE — only valid inside a component rendered under one of the two v1 mounts
 * (they bind `:tenantSlug`, and the `useMatch` below needs the real pathname).
 * It is deliberately NOT used by:
 *   - the slug-scoped auth pages (`/:tenantSlug/login`, `/verify`, …), which are
 *     SIBLINGS of the v1 tree, are not parked by this PR, and are repointed at
 *     v2 later, and
 *   - `lib/apiClient.ts`'s 401/403 redirects, which run outside React.
 * Those keep building their paths by hand — see the PR notes.
 *
 * The `?? ""` guard mirrors `useTenantBase()` and is unreachable in practice.
 */
export function useTenantV1Base(): string {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  // Matches the parked mount at its index (`/acme/v1`, splat empty) and at every
  // descendant (`/acme/v1/dashboard`). Cannot false-positive at the root mount:
  // no v1 route is shaped `/:tenantSlug/:param`, so the literal `v1` in the
  // second segment is only ever the parked mount's own static segment.
  const parked = useMatch("/:tenantSlug/v1/*") != null;
  return `/${tenantSlug ?? ""}${parked ? "/v1" : ""}`;
}
